import { DatePipe } from '@angular/common';
import { inject, Injectable, TemplateRef } from '@angular/core';
import { MediaListGroup } from '@modules/shared/media-picker/models/media-list-group';
import { MediaListItemContext } from '@modules/shared/media-picker/models/media-list-item-context';
import { MediaPickerDialogInputs } from '@modules/shared/media-picker/models/media-picker-dialog-inputs';
import { MediaPickerSelection } from '@modules/shared/media-picker/models/media-picker-selection';
import { MediaPickerValidator } from '@modules/shared/media-picker/models/media-picker-validator';
import { MediaPickerDialogService } from '@modules/shared/media-picker/services/media-picker-dialog.service';
import { Patient } from '@pixacare/domain';
import { FlattenedSequence } from '@pixacare/dtos';
import { FilterOperator, PaginationQuery } from '@pixacare/shared';
import { reportPaginationContext } from '@shared/models/pagination/pagination-context.config';
import { distinctUntilChanged, map, Observable } from 'rxjs';
import { SequencePaginationService } from './sequence-pagination.service';

export type OpenPatientSequencePickerOptions = {
  patient: Patient;
  preselectedMediaIds?: number[];
  preselectedFiles?: File[];
  maxSelectable?: number;
  validators?: MediaPickerValidator[];
  itemTemplate?: TemplateRef<MediaListItemContext>;
  label?: string;
  subtitle?: string;
  confirmLabel?: string;
  onUploadedFilesChange?: (files: File[]) => void;
};

@Injectable()
export class PatientSequencePickerService {
  private readonly sequencePaginationService = inject(SequencePaginationService);
  private readonly mediaPickerDialogService = inject(MediaPickerDialogService);
  private readonly datePipe = inject(DatePipe);

  private clientCode = '';
  private patientId?: number;

  readonly displayedSequences$ = this.sequencePaginationService.select();

  loadNextSequences(): void {
    this.sequencePaginationService.loadNextPage(this.clientCode);
  }

  private loadSequences(): void {
    this.sequencePaginationService.load(this.clientCode, {
      query: new PaginationQuery({
        orderBy: ['created_on|desc'],
        filter: [
          {
            prop: 'patient_id',
            op: FilterOperator.EQUAL,
            val: String(this.patientId),
          },
        ],
        size: reportPaginationContext.countPerPage,
      }),
      reset: true,
    });
  }

  open(opts: OpenPatientSequencePickerOptions): Observable<MediaPickerSelection | null> {
    this.clientCode = opts.patient.clientCode ?? '';
    this.patientId = opts.patient.id;
    this.loadSequences();

    const data: MediaPickerDialogInputs = {
      groups$: this.displayedSequences$.pipe(map((collection) => this.buildGroups(collection.data ?? []))),
      hasMore$: this.displayedSequences$.pipe(
        map((collection) => !!collection.context?.hasNextPage),
        distinctUntilChanged(),
      ),
      isLoadingMore$: this.displayedSequences$.pipe(
        map((collection) => !!collection.context?.isNextPageLoading),
        distinctUntilChanged(),
      ),
      onLoadMore: () => this.loadNextSequences(),
      preselectedMediaIds: opts.preselectedMediaIds,
      preselectedFiles: opts.preselectedFiles,
      maxSelectable: opts.maxSelectable,
      validators: opts.validators,
      itemTemplate: opts.itemTemplate,
      subtitle: opts.subtitle,
      confirmLabel: opts.confirmLabel,
      onUploadedFilesChange: opts.onUploadedFilesChange,
    };

    return this.mediaPickerDialogService.open(data, { size: 'l', label: opts.label });
  }

  private buildGroups(sequences: FlattenedSequence[]): MediaListGroup[] {
    const byDate = new Map<string, FlattenedSequence[]>();
    for (const sequence of sequences) {
      const createdOn = sequence.sequenceInstance?.createdOn;
      if (!createdOn) {
        continue;
      }
      const key = this.datePipe.transform(createdOn, 'yyyy-MM-dd')!;
      const bucket = byDate.get(key) ?? [];
      bucket.push(sequence);
      byDate.set(key, bucket);
    }
    return [...byDate.entries()]
      .sort(([a], [b]) => (a < b ? 1 : -1))
      .map(([key, group]) => ({
        label: this.datePipe.transform(key, 'mediumDate')!,
        items: group.flatMap((s) => s.medias ?? []).map((media) => ({ type: 'media' as const, media })),
      }))
      .filter((group) => group.items.length > 0);
  }
}
